import React from "react";
import { Col, Row, List, Typography } from "antd";

import { EuerDeclarationFields } from "../../../../../../../../api/graphql";
import {
  recalculateTotalFields,
  TOTAL_FIELDS,
} from "../../../../../../../../utils/euerFields";
import {
  ListScrollContainer,
  ElsterCodeCol,
  AmountCol,
  AmountInputWrapper,
} from "./styledComponents";
import euerDeclarationFields from "../euerDeclarationFields";
import AmountInput from "./AmountInput";
import { formatAmountCurrency } from "../../../../../../../../utils";
import EuerDeclarationFieldLink from "./EuerDeclarationFieldLink";
import EuerVatTaxAccountCheck, {
  EuerVatTaxAccountCheckBox,
} from "./EuerVatTaxAccountCheck";
import { useEuerVatTaxAccountCheck } from "./useEuerVatTaxAccountCheck";

const { Text } = Typography;

const NOT_EDITABLE_EUER_FIELDS = [...TOTAL_FIELDS];

// 186 = VAT paid to the tax office, 141 = VAT refunded by it.
const VAT_CHECK_FIELDS = ["186", "141"] as const;

export interface EuerDeclarationFieldsListProps {
  email: string;
  taxYear: number;
  fields: EuerDeclarationFields;
  autoCalculatedFields: EuerDeclarationFields;
  onChange: (fields: EuerDeclarationFields) => void;
}

const EuerDeclarationFieldsList = ({
  fields,
  email,
  taxYear,
  autoCalculatedFields,
  onChange,
}: EuerDeclarationFieldsListProps) => {
  // One lazy query for both lines; nothing is requested before a click.
  const vatCheck = useEuerVatTaxAccountCheck(email, taxYear);

  // Paid and refunded VAT in euros, incl. unsaved edits.
  const paidFieldValue = fields["186"] || 0;
  const refundedFieldValue = fields["141"] || 0;

  const handleAmountChange = (elsterField: string, value: number) => {
    onChange(recalculateTotalFields({ ...fields, [elsterField]: value }));
  };

  return (
    <ListScrollContainer>
      <List
        itemLayout="horizontal"
        dataSource={Array.from(euerDeclarationFields)}
        header={
          <Row>
            <ElsterCodeCol span={3}>
              <Text strong>Kennzahl</Text>
            </ElsterCodeCol>
            <Col span={16}>
              <Text strong>Bezeichnung</Text>
            </Col>
            <AmountCol span={5}>
              <Text strong>Summe</Text>
            </AmountCol>
          </Row>
        }
        renderItem={([elsterField, definition]) => (
          <List.Item style={{ flexWrap: "wrap" }}>
            <ElsterCodeCol span={3}>{definition.label}</ElsterCodeCol>
            <Col span={16}>
              <EuerDeclarationFieldLink
                email={email}
                taxYear={taxYear}
                code={elsterField}
                description={definition.description}
              />
            </Col>
            <AmountCol span={5}>
              <AmountInput
                editable={!NOT_EDITABLE_EUER_FIELDS.includes(elsterField)}
                strong={TOTAL_FIELDS.includes(elsterField)}
                value={fields[elsterField] || 0}
                onChange={(value: number) =>
                  handleAmountChange(elsterField, value)
                }
              />
              {!TOTAL_FIELDS.includes(elsterField) &&
                fields[elsterField] !== autoCalculatedFields[elsterField] && (
                  <AmountInputWrapper className="ant-typography ant-typography-secondary m-0">
                    {`Auto: ${formatAmountCurrency(
                      autoCalculatedFields[elsterField] || 0
                    )}`}
                  </AmountInputWrapper>
                )}
              {(VAT_CHECK_FIELDS as readonly string[]).includes(
                elsterField
              ) && (
                <EuerVatTaxAccountCheck
                  code={elsterField as (typeof VAT_CHECK_FIELDS)[number]}
                  fieldValue={fields[elsterField] || 0}
                  paidFieldValue={paidFieldValue}
                  refundedFieldValue={refundedFieldValue}
                  check={vatCheck}
                />
              )}
            </AmountCol>
            {(VAT_CHECK_FIELDS as readonly string[]).includes(elsterField) && (
              <EuerVatTaxAccountCheckBox
                code={elsterField as (typeof VAT_CHECK_FIELDS)[number]}
                fieldValue={fields[elsterField] || 0}
                paidFieldValue={paidFieldValue}
                refundedFieldValue={refundedFieldValue}
                check={vatCheck}
              />
            )}
          </List.Item>
        )}
      />
    </ListScrollContainer>
  );
};

export default EuerDeclarationFieldsList;
